import ExcelJS from "exceljs";
import { getRentersTenantsDashboardData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/rentersTenantsDashboardData";

/**
 * Export Renters/Tenants Dashboard Data to Excel
 * Creates tables matching the exact data structure for comparison with UI
 */
export async function exportRentersTenantsDashboardToExcel() {
  try {
    const data = getRentersTenantsDashboardData();
    const tenant = data.tenant;
    const monthlyData = data.monthlyData;
    const currentDate = new Date().toLocaleDateString("en-GB");

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== TENANT INFO SHEET ====================
    const tenantSheet = workbook.addWorksheet("Tenant Info", {
      properties: { tabColor: { argb: "FF3B82F6" } }
    });

    tenantSheet.columns = [{ width: 30 }, { width: 40 }];

    // Title
    tenantSheet.mergeCells("A1:B1");
    const titleCell = tenantSheet.getCell("A1");
    titleCell.value = "TENANT INFORMATION & BENCHMARKS";
    titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF3B82F6" }
    };
    tenantSheet.getRow(1).height = 35;

    let row = 3;
    addDataRow(tenantSheet, row++, "ID", tenant.id);
    addDataRow(tenantSheet, row++, "Company Name", tenant.companyName);
    addDataRow(tenantSheet, row++, "Building", tenant.building);
    addDataRow(tenantSheet, row++, "Location", tenant.location);
    addDataRow(tenantSheet, row++, "Floor Number", tenant.floorNumber);
    addDataRow(tenantSheet, row++, "Leased Area (sqm)", tenant.leasedArea);
    addDataRow(tenantSheet, row++, "Workstations", tenant.workstations);
    addDataRow(tenantSheet, row++, "Meeting Rooms", tenant.meetingRooms);
    addDataRow(tenantSheet, row++, "Conference Rooms", tenant.conferenceRooms);

    row++;
    addSectionHeader(tenantSheet, row++, "Lease Information");
    addDataRow(tenantSheet, row++, "Lease Start", tenant.leaseStart);
    addDataRow(tenantSheet, row++, "Lease End", tenant.leaseEnd);
    addDataRow(tenantSheet, row++, "Monthly Rent (€)", tenant.monthlyRent);

    row++;
    addSectionHeader(tenantSheet, row++, "Operating Details");
    addDataRow(
      tenantSheet,
      row++,
      "Operating Hours Start",
      tenant.operatingHours.start
    );
    addDataRow(
      tenantSheet,
      row++,
      "Operating Hours End",
      tenant.operatingHours.end
    );
    addDataRow(
      tenantSheet,
      row++,
      "Operating Days",
      tenant.operatingHours.weekdays.join(", ")
    );
    addDataRow(
      tenantSheet,
      row++,
      "Energy Cost per kWh (€)",
      tenant.energyCostPerKwh
    );
    addDataRow(
      tenantSheet,
      row++,
      "CO₂ Factor per kWh (kg)",
      tenant.co2FactorPerKwh
    );

    row++;
    addSectionHeader(tenantSheet, row++, "Target Benchmarks");
    addDataRow(
      tenantSheet,
      row++,
      "Utilization Rate Target (%)",
      tenant.benchmarks.utilizationRate
    );
    addDataRow(
      tenantSheet,
      row++,
      "Energy per Booking Target (kWh)",
      tenant.benchmarks.energyPerBooking
    );
    addDataRow(
      tenantSheet,
      row++,
      "Comfort Index Target (%)",
      tenant.benchmarks.comfortIndex
    );
    addDataRow(
      tenantSheet,
      row++,
      "Avg Booking Duration Target (hours)",
      tenant.benchmarks.avgBookingDuration
    );
    addDataRow(
      tenantSheet,
      row++,
      "CO₂ Awareness Target (%)",
      tenant.benchmarks.co2Awareness
    );

    row++;
    addDataRow(tenantSheet, row++, "Report Generated", currentDate);
    addDataRow(tenantSheet, row++, "Total Months", monthlyData.length);

    // ==================== BOOKING UTILIZATION SHEET ====================
    const utilizationSheet = workbook.addWorksheet("Booking Utilization", {
      properties: { tabColor: { argb: "FF10B981" } }
    });

    utilizationSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 12 }, // Rate %
      { width: 12 }, // Change %
      { width: 12 }, // Booked Slots
      { width: 15 }, // Available Slots
      { width: 20 }, // Peak Days
      { width: 18 }, // Top Space 1
      { width: 12 }, // Top Space 1 %
      { width: 18 }, // Top Space 2
      { width: 12 } // Top Space 2 %
    ];

    createBookingUtilizationTable(utilizationSheet, monthlyData);

    // ==================== HOURLY UTILIZATION SHEET ====================
    const hourlySheet = workbook.addWorksheet("Hourly Utilization", {
      properties: { tabColor: { argb: "FF22C55E" } }
    });

    createHourlyUtilizationTable(hourlySheet, monthlyData);

    // ==================== ENERGY PER BOOKING SHEET ====================
    const energySheet = workbook.addWorksheet("Energy Per Booking", {
      properties: { tabColor: { argb: "FFF59E0B" } }
    });

    energySheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Avg Energy
      { width: 12 }, // Change %
      { width: 15 }, // Total Energy
      { width: 15 }, // Total Cost
      { width: 20 }, // Peak Hours
      { width: 15 }, // Top Consumer 1
      { width: 15 }, // Top Consumer 2
      { width: 15 }, // Top Consumer 3
      { width: 12 }, // Meeting Rooms %
      { width: 12 }, // Conference %
      { width: 12 }, // Open Office %
      { width: 12 }, // Breakout %
      { width: 15 } // Benchmark
    ];

    createEnergyPerBookingTable(energySheet, monthlyData);

    // ==================== FLOOR ENERGY DISTRIBUTION SHEET ====================
    const floorEnergySheet = workbook.addWorksheet(
      "Floor Energy Distribution",
      {
        properties: { tabColor: { argb: "FFEF4444" } }
      }
    );

    floorEnergySheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Floor 1 %
      { width: 12 }, // Floor 1 Change
      { width: 15 }, // Floor 1 Emissions
      { width: 15 }, // Floor 2 %
      { width: 12 }, // Floor 2 Change
      { width: 15 }, // Floor 2 Emissions
      { width: 15 }, // Floor 3 %
      { width: 12 }, // Floor 3 Change
      { width: 15 }, // Floor 3 Emissions
      { width: 15 }, // Floor 4 %
      { width: 12 }, // Floor 4 Change
      { width: 15 } // Floor 4 Emissions
    ];

    createFloorEnergyTable(floorEnergySheet, monthlyData);

    // ==================== COMFORT SATISFACTION SHEET ====================
    const comfortSheet = workbook.addWorksheet("Comfort Satisfaction", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });

    comfortSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Overall Score
      { width: 12 }, // Change %
      { width: 15 }, // Energy Analytics
      { width: 18 }, // Dynamic Occupancy
      { width: 18 }, // Utilization Analytics
      { width: 15 }, // Energy Use
      { width: 15 } // CO2 Emissions
    ];

    createComfortSatisfactionTable(comfortSheet, monthlyData);

    // ==================== BOOKING DURATION SHEET ====================
    const durationSheet = workbook.addWorksheet("Booking Duration", {
      properties: { tabColor: { argb: "FF06B6D4" } }
    });

    durationSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 12 }, // Avg Duration
      { width: 12 }, // Change %
      { width: 15 }, // Total Bookings
      { width: 12 }, // Efficiency %
      { width: 25 }, // Peak Days
      { width: 25 }, // Peak Hours
      { width: 10 }, // < 1hr
      { width: 10 }, // 1-2hrs
      { width: 10 }, // 2-4hrs
      { width: 10 }, // 4-6hrs
      { width: 10 } // 6+hrs
    ];

    createBookingDurationTable(durationSheet, monthlyData);

    // ==================== ROOM EFFICIENCY SHEET ====================
    const roomSheet = workbook.addWorksheet("Room Efficiency", {
      properties: { tabColor: { argb: "FF14B8A6" } }
    });

    createRoomEfficiencyTable(roomSheet, monthlyData);

    // ==================== CO2 AWARENESS SHEET ====================
    const co2Sheet = workbook.addWorksheet("CO2 Awareness", {
      properties: { tabColor: { argb: "FFEF4444" } }
    });

    co2Sheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Overall Score
      { width: 12 }, // Change %
      { width: 15 }, // Energy Efficiency
      { width: 15 }, // Waste Diversion
      { width: 18 }, // Water Conservation
      { width: 12 }, // Current Score
      { width: 15 }, // Positive Actions
      { width: 12 }, // Warning Zone
      { width: 12 } // Benchmark
    ];

    createCO2AwarenessTable(co2Sheet, monthlyData);

    // ==================== MONTHLY EMISSIONS SHEET ====================
    const emissionsSheet = workbook.addWorksheet("Monthly Emissions", {
      properties: { tabColor: { argb: "FFDC2626" } }
    });

    createMonthlyEmissionsTable(emissionsSheet, monthlyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Renters-Tenants-Data-${tenant.companyName.replace(
      /\s+/g,
      "-"
    )}-${currentDate.replace(/\//g, "-")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== HELPER FUNCTIONS ====================

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } }
    };
  });
  sheet.getRow(1).height = 30;
}

function styleDataCell(cell, value, isEven, format = "text") {
  cell.value = value;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: isEven ? "FFFFFFFF" : "FFF9FAFB" }
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (format === "number") {
    cell.numFmt = "#,##0";
  } else if (format === "decimal") {
    cell.numFmt = "0.0";
  } else if (format === "currency") {
    cell.numFmt = "€#,##0.00";
  } else if (format === "percent") {
    cell.numFmt = '0.0"%"';
    if (value !== null && value !== undefined && value !== "N/A") {
      cell.font = { color: { argb: value >= 0 ? "FF10B981" : "FFEF4444" } };
    }
  }
}

function createBookingUtilizationTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Utilization\nRate %",
    "Change\n%",
    "Booked\nSlots",
    "Available\nSlots",
    "Peak Days",
    "Top Space 1",
    "Space 1\nUtil %",
    "Top Space 2",
    "Space 2\nUtil %"
  ];

  createTableHeader(sheet, headers, "FF10B981");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const util = month.bookingUtilization;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(sheet.getCell(rowNum, 3), util.rate, isEven, "number");
    styleDataCell(
      sheet.getCell(rowNum, 4),
      util.changePercent || "N/A",
      isEven,
      util.changePercent ? "percent" : "text"
    );
    styleDataCell(sheet.getCell(rowNum, 5), util.bookedSlots, isEven, "number");
    styleDataCell(
      sheet.getCell(rowNum, 6),
      util.availableSlots,
      isEven,
      "number"
    );
    styleDataCell(sheet.getCell(rowNum, 7), util.peakDays.join(" & "), isEven);
    styleDataCell(
      sheet.getCell(rowNum, 8),
      util.topSpaces[0]?.name || "",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      util.topSpaces[0]?.utilization || "",
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      util.topSpaces[1]?.name || "",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      util.topSpaces[1]?.utilization || "",
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createHourlyUtilizationTable(sheet, data) {
  sheet.columns = Array(14).fill({ width: 10 });

  const headers = [
    "Month",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00"
  ];

  createTableHeader(sheet, headers, "FF22C55E");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);

    month.bookingUtilization.hourlyUtilization.forEach((hourData, i) => {
      styleDataCell(
        sheet.getCell(rowNum, i + 2),
        hourData.rate,
        isEven,
        "number"
      );
    });

    sheet.getRow(rowNum).height = 20;
  });
}

function createEnergyPerBookingTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Avg Energy\n(kWh)",
    "Change\n%",
    "Total Energy\n(kWh)",
    "Total Cost\n(€)",
    "Peak Hours",
    "Top\nConsumer 1",
    "Top\nConsumer 2",
    "Top\nConsumer 3",
    "Meeting\nRooms %",
    "Conference\nRooms %",
    "Open\nOffice %",
    "Breakout\nAreas %",
    "Benchmark\nComparison %"
  ];

  createTableHeader(sheet, headers, "FFF59E0B");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const energy = month.energyPerBooking;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      energy.avgEnergy,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      energy.changePercent || "N/A",
      isEven,
      energy.changePercent ? "percent" : "text"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      energy.totalEnergy,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      energy.totalCost,
      isEven,
      "currency"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      energy.peakHours.join(", "),
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      energy.topConsumers[0] || "",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      energy.topConsumers[1] || "",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      energy.topConsumers[2] || "",
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      energy.spaceBreakdown.meetingRooms,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      energy.spaceBreakdown.conferenceRooms,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      energy.spaceBreakdown.openOffice,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 14),
      energy.spaceBreakdown.breakoutAreas,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 15),
      energy.benchmarkComparison,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createFloorEnergyTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Meeting Rooms\n%",
    "Meeting Rooms\nChange %",
    "Meeting Rooms\nEmissions (kg)",
    "Conference Rooms\n%",
    "Conference Rooms\nChange %",
    "Conference Rooms\nEmissions (kg)",
    "Open Office\n%",
    "Open Office\nChange %",
    "Open Office\nEmissions (kg)",
    "Breakout Areas\n%",
    "Breakout Areas\nChange %",
    "Breakout Areas\nEmissions (kg)"
  ];

  createTableHeader(sheet, headers, "FFEF4444");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const floors = month.energyPerBooking.floorEnergyDistribution;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);

    floors.forEach((floor, floorIndex) => {
      const baseCol = 3 + floorIndex * 3;
      styleDataCell(
        sheet.getCell(rowNum, baseCol),
        floor.percentage,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, baseCol + 1),
        floor.change,
        isEven,
        "percent"
      );
      styleDataCell(
        sheet.getCell(rowNum, baseCol + 2),
        floor.emissions,
        isEven,
        "number"
      );
    });

    sheet.getRow(rowNum).height = 20;
  });
}

function createComfortSatisfactionTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Overall\nScore %",
    "Change\n%",
    "Energy\nAnalytics %",
    "Dynamic\nOccupancy %",
    "Utilization\nAnalytics %",
    "Monthly\nEnergy Use (kWh)",
    "Monthly\nCO₂ Emissions (kg)"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const comfort = month.comfortSatisfaction;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      comfort.overallScore,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      comfort.changePercent || "N/A",
      isEven,
      comfort.changePercent ? "percent" : "text"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      comfort.energyAnalytics,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      comfort.dynamicOccupancy,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      comfort.utilizationAnalytics,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      comfort.monthlyTrend.energyUse,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      comfort.monthlyTrend.co2Emissions,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createBookingDurationTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Avg Duration\n(hours)",
    "Change\n%",
    "Total\nBookings",
    "Efficiency\n%",
    "Peak Booking Days",
    "Peak Booking Hours",
    "< 1hr\nBookings",
    "1-2hrs\nBookings",
    "2-4hrs\nBookings",
    "4-6hrs\nBookings",
    "6+hrs\nBookings"
  ];

  createTableHeader(sheet, headers, "FF06B6D4");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const duration = month.bookingDuration;
    const dist = duration.durationDistribution;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 3),
      duration.avgDuration,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      duration.changePercent || "N/A",
      isEven,
      duration.changePercent ? "percent" : "text"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      duration.totalBookings,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      duration.efficiency,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      duration.peakBookingDays.join(", "),
      isEven
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      duration.peakBookingHours.join(", "),
      isEven
    );
    styleDataCell(sheet.getCell(rowNum, 9), dist.lessThan1Hr, isEven, "number");
    styleDataCell(
      sheet.getCell(rowNum, 10),
      dist.oneToTwoHrs,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      dist.twoToFourHrs,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 12),
      dist.fourToSixHrs,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 13),
      dist.moreThanSixHrs,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createRoomEfficiencyTable(sheet, data) {
  sheet.columns = [
    { width: 15 }, // Month
    { width: 18 }, // Room Name
    { width: 12 }, // Avg Duration
    { width: 12 }, // Efficiency
    { width: 12 } // Bookings
  ];

  const headers = [
    "Month",
    "Room Name",
    "Avg Duration\n(hours)",
    "Efficiency\n%",
    "Total\nBookings"
  ];

  createTableHeader(sheet, headers, "FF14B8A6");

  let rowNum = 2;
  data.forEach((month) => {
    const rooms = month.bookingDuration.roomEfficiency;

    rooms.forEach((room) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), room.room, isEven);
      styleDataCell(
        sheet.getCell(rowNum, 3),
        room.avgDuration,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 4),
        room.efficiency,
        isEven,
        "number"
      );
      styleDataCell(sheet.getCell(rowNum, 5), room.bookings, isEven, "number");

      sheet.getRow(rowNum).height = 20;
      rowNum++;
    });
  });
}

function createCO2AwarenessTable(sheet, data) {
  const headers = [
    "Month",
    "Date",
    "Overall\nScore %",
    "Change\n%",
    "Energy\nEfficiency %",
    "Waste\nDiversion %",
    "Water\nConservation %",
    "Current\nScore %",
    "Positive\nActions %",
    "Warning\nZone %",
    "Benchmark\n%"
  ];

  createTableHeader(sheet, headers, "FFEF4444");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const co2 = month.co2Awareness;
    const carbonScore = co2.carbonScore;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(sheet.getCell(rowNum, 2), month.date, isEven);
    styleDataCell(sheet.getCell(rowNum, 3), co2.overallScore, isEven, "number");
    styleDataCell(
      sheet.getCell(rowNum, 4),
      co2.changePercent || "N/A",
      isEven,
      co2.changePercent ? "percent" : "text"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      co2.energyEfficiency,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      co2.wasteDiversion,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      co2.waterConservation,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      carbonScore.currentScore,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      carbonScore.positiveActions,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      carbonScore.warningZone,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 11),
      carbonScore.benchmark,
      isEven,
      "number"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createMonthlyEmissionsTable(sheet, data) {
  sheet.columns = [
    { width: 15 }, // Month
    { width: 12 }, // Jan
    { width: 12 }, // Feb
    { width: 12 }, // Mar
    { width: 12 }, // Apr
    { width: 12 }, // May
    { width: 12 }, // Jun
    { width: 12 }, // Jul
    { width: 12 }, // Aug
    { width: 12 }, // Sep
    { width: 12 }, // Oct
    { width: 12 }, // Nov
    { width: 12 } // Dec
  ];

  const headers = [
    "Month",
    "Jan\nEmissions",
    "Feb\nEmissions",
    "Mar\nEmissions",
    "Apr\nEmissions",
    "May\nEmissions",
    "Jun\nEmissions",
    "Jul\nEmissions",
    "Aug\nEmissions",
    "Sep\nEmissions",
    "Oct\nEmissions",
    "Nov\nEmissions",
    "Dec\nEmissions"
  ];

  createTableHeader(sheet, headers, "FFDC2626");

  data.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = index % 2 === 0;
    const emissions = month.co2Awareness.monthlyEmissions;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);

    emissions.forEach((emission, i) => {
      styleDataCell(sheet.getCell(rowNum, i + 2), emission, isEven, "number");
    });

    sheet.getRow(rowNum).height = 20;
  });
}

// Default export
export default {
  exportRentersTenantsDashboardToExcel
};
